/**
 * Cache wrapper for the Rigs list. TtlCache (15-min, single "all" entry, in-flight coalescing)
 * around `API.Data.Rigs.get.all()`. Rigs are global reference data that change rarely, so a shared
 * cache spares `get()` callers a fresh fetch.
 *
 * The Scheduling board reads via `refresh()`, not `get()` — on mount and on its refresh gesture,
 * which re-reads the fleet layers as one unit. Rigs change rarely so the TTL would usually be
 * fine, but a rig list on a stale TTL while trucks/robots are live would let a truck's shift
 * colour disagree with the board it is drawn on.
 */

import { Rig } from "@domain/CRM/rig";
import { TtlCache } from "../../../cache/ttlCache";
import { API } from "../../../api";

const LIST_TTL_MS = 15 * 60 * 1000;
const LIST_KEY = "all" as const;

/** The one query behind both the TTL fetcher and `refresh()`; `fresh` also bypasses the
 *  server-side Airtable cache (`?fresh=1`). */
const fetchRigs = (fresh?: boolean): Promise<Rig[]> => API.Data.Rigs.get.all(fresh);

class CachedRigsList {
	private cache = new TtlCache<typeof LIST_KEY, Rig[]>(LIST_TTL_MS, () => fetchRigs());

	/** Synchronous TTL cache hit, or null if missing/expired. */
	public peek(): Rig[] | null {
		return this.cache.peek(LIST_KEY);
	}

	public get(): Promise<Rig[]> {
		return this.cache.get(LIST_KEY);
	}

	/**
	 * Bypass BOTH caches — the server's (`?fresh=1`) and this TTL entry — and overwrite the entry
	 * with the live result. Used by the Scheduling board on mount and on its refresh-in-place
	 * gesture. Unlike `get()`, concurrent callers each fetch — this is a deliberate bypass, not a
	 * join. A failed fetch leaves the previous entry in place.
	 */
	public async refresh(): Promise<Rig[]> {
		const rigs = await fetchRigs(true);
		this.cache.set(LIST_KEY, rigs);
		return rigs;
	}

	public clear(): void {
		this.cache.clear();
	}
}

export class CachedRigsAPI {
	public list = new CachedRigsList();
}
